import { createApi, EndpointBuilder } from "@reduxjs/toolkit/query/react";
import { appAxiosBaseQuery } from "./axiosBaseQuery";
import { publicAxiosBaseQuery } from "./publicAxiosBaseQuery";
import { IProfile } from "../models/directory/profile";
import { IGetProfileRequest } from "../models/directory/request/directory/IProfileRequest";
import { IPaginatedResponse } from "../models/common/IPaginatedResponse";
import { ImainMenu } from "./models/MainMenuModal";
import { IHomeBannerData, IHomeWhatsNewItem } from "./models/HomeModal";
import { IArticle } from "../models/thoughtware/articlet";
import {
  IEventRegistrationResponse,
  IEventResponse,
} from "./models/EventModal";
import {
  ApiResponse,
  MarketplaceJob,
  PagedResult,
} from "../models/marketplace/marketplaceJob";
import { MarketplaceAlumniProfileResponse } from "../models/marketplace/marketplaceAlumniProfile";
import {
  IAdminAlumniDirectoryResponse,
  AdminAlumniDirectoryExportRequest,
  AdminAlumniDirectoryExportResponse,
} from "./models/AdminAlumniDirectoryModal";
import { IViewProfile } from "./models/ProfileModal";
import { IProfileSetupDataResponse } from "./models/ProfileSetupModal";
import { ILandingPageResponse } from "./models/LandingModal";
import { RegisterForm, AlumniRegisterResponse, InitiateCiamRegistrationRequest, CiamRegistrationResponse } from "./models/LandingHeroSection.types";
import type { NotificationGroup, NotificationItemModel } from "../features/Notifications/types";
import type { NotificationListItemDto, NotificationsPageDto } from "../features/Notifications/notificationDto";
import type { RootState } from "./store";
import { appAxiosInstance } from "./getAxiosInstance";

const INTERNAL_EMAIL_DOMAIN = "@deloitte.com";

type ChatUserConversation = {
  id?: string;
  portalUserId?: string;
  name?: string;
  displayName?: string;
  designation?: string;
  time?: string;
  avatar?: string;
  profilePictureUrl?: string;
  profilePhotoUrl?: string;
  resourceId?: number;
  conversationId: string;
  threadId: string;
  topic: string;
  about?: string;
  participantIds: string[];
  organiserId: string;
  lastActivityAt: string;
};

type GetOrCreateChatTokenRequest = {
  portalUserId: string;
  displayName?: string;
  resourceId?: number;
  profilePictureUrl?: string;
};

type AcsChatTokenResponse = {
  acsUserId: string;
  token: string;
  expiresOn: string;
};

type CreateChatThreadRequest = {
  organiserId: string;
  participantPortalUserIds: string[];
  participantIds?: string;
  topic?: string;
};

type GetChatMessagesRequest = {
  threadId: string;
  userId?: string;
  organiserId?: string;
  take?: number;
};

type SendChatMessageRequest = {
  content: string;
  senderId: string;
  threadId: string;
  type: string;
}

type EditChatThreadMessageRequest = {
  threadId: string;
  messageId: string;
  content: string;
};

type DeleteChatThreadMessageRequest = {
  threadId: string;
  messageId: string;
};

type UploadChatThreadAttachmentsRequest = {
  threadId: string;
  files: File[];
};

type DownloadChatThreadAttachmentRequest = {
  threadId: string;
  userId: string;
  attachmentUrl: string;
  fileName: string;
  contentType?: string;
};

type ChatAttachmentResponse = {
  name: string;
  url: string;
  previewUrl?: string;
  contentType: string;
  sizeInBytes: number;
};

type SendBulkEmailRequest = {
  campaignId?: string;
  subject: string;
  body: string;
  cc?: string;
  bcc?: string;
  isHtmlBody?: boolean;
  filters?: BulkEmailFilters;
  attachmentPath?: string;
  attachmentFileName?: string;
  attachmentContentType?: string;
};

type BulkEmailFilters = {
  location?: string;
  industry?: string;
  gender?: string;
  function?: string;
  designation?: string;
  lastDesignation?: string;
  company?: string;
  tenureAtDeloitte?: string;
};

type UploadBulkEmailAttachmentResponse = {
  fileName: string;
  contentType: string;
  attachmentPath: string;
};

type SendBulkEmailResponse = {
  successCount: number;
  failedCount: number;
  errors?: string[];
};

type BulkEmailDraft = {
  campaignId: string;
  subject: string;
  bodyPreview: string;
  status: string;
  filterSnapshot?: string | null;
  targetRecipientCount: number;
  lastUpdatedOnUtc?: string | null;
};

type SaveBulkEmailDraftRequest = {
  campaignId?: string;
  subject: string;
  body: string;
  filters?: BulkEmailFilters;
  targetRecipientCount: number;
  attachmentPath?: string;
};

type SaveBulkEmailDraftResponse = {
  campaignId?: string;
};

type DeleteBulkEmailDraftRequest = {
  draftId: string | number;
};

type DeleteBulkEmailDraftResponse = {
  draftId: string | number;
  isDeleted: boolean;
};

type MarkChatMessageReadRequest = {
  threadId: string;
  userId: string;
  messageId: string;
};

type GetUnreadChatMessageCountRequest = {
  threadId: string;
  userId: string;
};

type UnreadChatMessageCountResponse = {
  threadId: string;
  userId: string;
  unreadCount: number;
  lastReadAt?: string | null;
};

type SetChatBlockStatusRequest = {
  targetPortalUserId: string;
  isBlocked: boolean;
};

type BlockedChatUserResponse = {
  blockedPortalUserId: string;
  blockedDisplayName?: string;
  blockedAt: string;
};

export type PendingReviewRequestItem = {
  id: number;
  name: string;
  title: string;
  type: string;
  profilePic: string;
  createdAtDisplay: string;
  createdAt?: string;
};

type PendingReviewRequestsResponse = {
  success: boolean;
  message: string;
  data: {
    pagination: {
      totalRecords: number;
      totalPages: number;
      currentPage: number;
      pageSize: number;
    };
    items: PendingReviewRequestItem[];
  };
  statusCode: number;
};

export type OnboardingManualData = {
  profilePic?: string;
  name?: string;
  requestMessage?: string;
  createdAtDisplay?: string;
  // Account Details
  firstName?: string;
  lastName?: string;
  email?: string;
  dateOfBirth?: string;
  gender?: string;
  mobilePhone?: string;
  currentWorkLocation?: string;
  currentLocation?: string;
  // Experience at Deloitte
  jobTitleAtCompany?: string;
  functionDeptAtCompany?: string;
  locationAtCompany?: string;
  employeeId?: string;
  startDate?: string;
  endDate?: string;
  // Current Employment
  employmentStatus?: string;
  currentCompany?: string;
  currentJobTitle?: string;
  levelOfRole?: string;
  businessEmailID?: string;
  currentEmployemntStartDate?: string;
  workExperienceYears?: number;
  // Education
  educations?: { id: number; empId: number; institution: string; course: string; year: number }[];
};

type OnboardingManualDetailsResponse = {
  success: boolean;
  message: string;
  data: OnboardingManualData;
  statusCode: number;
};

export type AdminFeedbackItem = {
  id: number;
  name: string;
  subject: string;
  description: string;
  type: string;
  profilePic: string;
  createdAtDisplay: string;
};

type AdminFeedbackResponse = {
  success: boolean;
  message: string;
  data: {
    pagination: { totalRecords: number; pageNumber: number; pageSize: number };
    items: AdminFeedbackItem[];
  };
  statusCode: number;
};

export type ContactSupportRequestDetail = {
  requestID: number;
  name: string;
  title: string;
  createdAtDisplay: string;
  phoneCode: string;
  phoneNumber: string;
  emailID: string;
  subject: string;
  message: string;
  attchamentName: string;
  attchamentUrl: string;
};

type ContactSupportRequestDetailResponse = {
  success: boolean;
  message: string;
  data: ContactSupportRequestDetail;
  statusCode: number;
};

type AddGroupChatParticipantsRequest = {
  threadId: string;
  participantPortalUserIds: string[];
};

type SetGroupChatAdminRequest = {
  threadId: string;
  portalUserId: string;
  isAdmin: boolean;
};

type UpdateGroupChatAboutRequest = {
  threadId: string;
  about: string;
};

type GroupChatParticipantsResponse = {
  threadId: string;
  organiserId: string;
  adminIds: string[];
  participantIds: string[];
  members: ChatUserConversation[];
};

type ChatThreadResponse = {
  id: string;
  resourceId?: number;
  name: string;
  designation: string;
  lastMessage: string;
  time: string;
  avatar: string;
  conversationId: string;
  threadId: string;
  topic: string;
  about: string;
  participantIds: string[];
  organiserId: string;
  lastActivityAt: string;
};

export const alumniPortalApi = createApi({
  reducerPath: "alumniPortalApi",
  baseQuery: appAxiosBaseQuery({ baseUrl: "/" }),
  tagTypes: [
    "followProfile",
    "unfollowProfile",
    "bookmarkArticle",
    "removeBookmarkArticle",
    "bookmarkWhatsNew",
    "removeBookmarkWhatsNew",
    "bookmarkEvents",
    "removeBookmarkEvents",
    "Profile",
    "MarketplaceJobs",
    "ChatBlockSettings",
    "Notifications",
    "BulkEmailDrafts",
    "BulkEmailHistory",
  ],
  endpoints: (builder: EndpointBuilder<any, any, any>) => {
    return {
      applyJob: builder.mutation<any, FormData>({
        query: (formData) => ({
          url: `api/careers/MarketplaceJobs/apply`,
          method: "POST",
          data: formData,
        }),
      }),
      getAluminiProfileList: builder.query<
        IPaginatedResponse<IProfile>,
        IGetProfileRequest
      >({
        query: (profileRequest) => ({
          url: `api/People/Profile/profiles`,
          method: "GET",
          params: {
            ...profileRequest,
          },
        }),
        providesTags: ["followProfile", "unfollowProfile"],
      }),
      followAlumni: builder.mutation<
        { message: string },
        { followerResourceId: number; followingResourceId: number }
      >({
        query: ({ followerResourceId, followingResourceId }) => ({
          url: "api/People/Profile/follow",
          method: "POST",
          data: {
            followerResourceId,
            followingResourceId,
          },
        }),
        invalidatesTags: ["followProfile", "Profile"],
      }),
      unfollowAlumni: builder.mutation<
        { message: string },
        { followerResourceId: number; followingResourceId: number }
      >({
        query: ({ followerResourceId, followingResourceId }) => ({
          url: "api/People/Profile/unfollow",
          method: "POST",
          data: {
            followerResourceId,
            followingResourceId,
          },
        }),
        invalidatesTags: ["unfollowProfile", "Profile"],
      }),
      getLocations: builder.query<{ label: string; value: string }[], void>({
        query: () => ({
          url: "api/People/MasterData/locations",
          method: "GET",
        }),
      }),

      getDesignations: builder.query<{ label: string; value: string }[], void>({
        query: () => ({
          url: "api/People/MasterData/designations",
          method: "GET",
        }),
      }),

      getFunctions: builder.query<{ label: string; value: string }[], void>({
        query: () => ({
          url: "api/People/MasterData/functions",
          method: "GET",
        }),
      }),

      getCompanyList: builder.query<{ label: string; value: string }[], void>({
        query: () => ({
          url: "api/People/MasterData/companyList",
          method: "GET",
        }),
      }),

      getLastDesignationList: builder.query<
        { label: string; value: string }[],
        void
      >({
        query: () => ({
          url: "api/People/MasterData/lastDesignationList",
          method: "GET",
        }),
      }),

      getIndustries: builder.query<{ label: string; value: string }[], void>({
        query: () => ({
          url: "api/People/MasterData/industries",
          method: "GET",
        }),
      }),
      getMasterMenu: builder.query<ImainMenu[], { userId: number; userRole: string }>({
        query: ({ userId, userRole }) => ({
          url: `api/Alumni/menu/${userId}`,
          method: "GET",
          params: { userRole },
        }),
        transformResponse: (response: any) => response?.data ?? [],
      }),

      //Home
      getHomeBanner: builder.query<IHomeBannerData, number>({
        query: (userId) => ({
          url: `api/Content/Home/banner/${userId}`,
          method: "GET",
        }),
        transformResponse: (response: any) => response?.data ?? [],
      }),
      getHomeWhatsNew: builder.query<IHomeWhatsNewItem[], number>({
        query: (userId) => ({
          url: `api/Content/Home/whatsNew/${userId}`,
          method: "GET",
        }),
        transformResponse: (response: any) => response?.data ?? [],
      }),
      bookmarkWhatsNew: builder.mutation<
        boolean,
        { cardId: number; userId: any }
      >({
        query: ({ userId, cardId }) => ({
          url: `api/Content/Home/${cardId}/bookmark?userId=${userId}`,
          method: "POST",
        }),
        transformResponse: (response: any) => response?.data ?? [],
        invalidatesTags: ["bookmarkWhatsNew", "removeBookmarkWhatsNew"],
      }),

      //Events
      getEvent: builder.query<
        IEventResponse,
        { eventType: string; userId: number,isBookmarkedOnly?: boolean }
      >({
        query: ({ eventType, userId, isBookmarkedOnly }) => ({
          url: `api/People/Events/${eventType}/${userId}?pageNumber=1&pageSize=0${isBookmarkedOnly ? "&isBookmarkedOnly=true" : ""}`,
          method: "GET",
        }),
        providesTags: ["Events"],
      }),
      eventRegistration: builder.mutation<
        IEventRegistrationResponse,
        { eventId: number; userId: number; isRegistered: boolean }
      >({
        query: ({ eventId, userId, isRegistered }) => ({
          url: "api/People/Events/registration",
          method: "POST",
          data: {
            eventId,
            userId,
            isRegistered,
          },
        }),
        invalidatesTags: ["Events"],
      }),
      bookmarkEvents: builder.mutation<
        boolean,
        { cardId: number; userId: any }
      >({
        query: ({ userId, cardId }) => ({
          url: `api/People/Events/${cardId}/bookmark?userId=${userId}`,
          method: "POST",
        }),
        transformResponse: (response: any) => response?.data ?? [],
        invalidatesTags: ["bookmarkEvents", "removeBookmarkEvents"],
      }),

      getMarketplaceJobs: builder.query<
        ApiResponse<PagedResult<MarketplaceJob>>,
        { userId: number; pageNumber: number; pageSize: number }
      >({
  query: (params) => ({
    url: "api/careers/MarketplaceJobs",
    method: "GET",
    params,
    headers: {
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
      Expires: "0",
    },
  }),
  transformResponse: (response: ApiResponse<PagedResult<MarketplaceJob>>) => {
    const isValid =
      !!response &&
      !!response.data &&
      Array.isArray(response.data.items) &&
      typeof response.data.totalCount === "number";

    if (!isValid) {
      throw new Error(
        "Invalid MarketplaceJobs payload from gateway. Expected paged jobs response."
      );
    }

    return response;
  },
  providesTags: ["MarketplaceJobs"],
}),

      getMarketplaceJobsSearch: builder.query<
        ApiResponse<PagedResult<MarketplaceJob>>,
        {
          userId?: number;
          pageNumber: number;
          pageSize: number;
          searchText?: string;
          location?: string;
          experienceLevelTagId?: number;
          employmentTypeTagId?: number;
          workModeTagId?: number;
        }
      >({
        query: (params) => ({
          url: "api/careers/MarketplaceJobs/search",
          method: "GET",
          params,
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
        }),
        transformResponse: (response: ApiResponse<PagedResult<MarketplaceJob>>) => {
          const isValid =
            !!response &&
            !!response.data &&
            Array.isArray(response.data.items) &&
            typeof response.data.totalCount === "number";

          if (!isValid) {
            throw new Error(
              "Invalid MarketplaceJobs search payload from gateway. Expected paged jobs response."
            );
          }

          return response;
        },
        providesTags: ["MarketplaceJobs"],
      }),

      getMarketplaceAlumniProfile: builder.query<
        MarketplaceAlumniProfileResponse,
        number
      >({
        query: (employeeId) => ({
          url: `api/careers/MarketplaceJobs/alumni-profile/${employeeId}`,
          method: "GET",
        }),
      }),

      updateMarketplaceJob: builder.mutation<
        { success: boolean; message: string; data?: { status?: number; message?: string }; statusCode?: number },
        FormData
      >({
        query: (payload) => ({
          url: "api/careers/MarketplaceJobs/update",
          method: "PUT",
          data: payload,
        }),
        invalidatesTags: ["MarketplaceJobs"],
      }),


      getChatUsers: builder.query<ChatUserConversation[], void>({
        query: () => ({
          url: "api/communication/Chat/conversations",
          method: "GET",
          params: {
            includeLatestMessage: true,
          },
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data?.messages ?? response?.data ?? response ?? [];
          if (!Array.isArray(normalized)) {
            return [];
          }

          return normalized.map((item: any) => ({
            ...item,
            portalUserId:
              item?.portalUserId ??
              item?.id ??
              "",
            name:
              item?.name ??
              item?.displayName ??
              "",
            about:
              item?.about ??
              item?.About ??
              "",
            avatar:
              item?.avatar ??
              item?.profilePictureUrl ??
              item?.profilePhotoUrl ??
              "",
          }));
        },
      }),

      searchChatUsers: builder.query<ChatUserConversation[], string>({
        queryFn: async (searchText, api, _extraOptions, baseQuery) => {
          const state = api.getState() as RootState;
          const authEmail =
            state.auth.appUserAccount?.data?.emailId ||
            state.auth.appUserAccount?.data?.email ||
            state.auth.appUserAccount?.emailId ||
            state.auth.appUserAccount?.email ||
            state.auth.msGraphUser?.mail ||
            state.auth.msGraphUser?.userPrincipalName ||
            "";

          const rawImpersonatedPortalUserId = localStorage.getItem("impersonate") || "";
          const impersonatedPortalUserId =
            rawImpersonatedPortalUserId.includes("react-aria")
              ? ""
              : rawImpersonatedPortalUserId;
          const effectivePortalUserId = impersonatedPortalUserId || authEmail;

          const isDeloitteUser = effectivePortalUserId
            .trim()
            .toLowerCase()
            .endsWith(INTERNAL_EMAIL_DOMAIN);

          const url = isDeloitteUser
            ? "api/communication/Chat/users"
            : "api/communication/Chat/users/alumni";

          const result = await baseQuery({
            url,
            method: "GET",
            params: searchText
              ? {
                  searchText,
                }
              : undefined,
          });

          if (result.error) {
            return { error: result.error };
          }

          const response = result.data as any;
          const normalized = response?.data?.messages ?? response?.data ?? response ?? [];
          if (!Array.isArray(normalized)) {
            return { data: [] };
          }

          return {
            data: normalized.map((item: any) => ({
            ...item,
            id:
              item?.id ??
              item?.portalUserId ??
              item?.email ??
              item?.userId ??
              "",
            portalUserId:
              item?.portalUserId ??
              item?.email ??
              item?.userId ??
              item?.id ??
              "",
            name:
              item?.name ??
              item?.displayName ??
              item?.fullName ??
              "",
            avatar:
              item?.avatar ??
              item?.profileImage ??
              item?.profilePicture ??
              item?.image ??
              item?.photoUrl ??
              item?.profilePictureUrl ??
              item?.profilePhotoUrl ??
              "",
            })),
          };
        },
      }),

      getOrCreateChatToken: builder.mutation<AcsChatTokenResponse, GetOrCreateChatTokenRequest>({
        query: (payload) => ({
          url: "api/communication/Chat/createacsuser",
          method: "POST",
          data: payload,
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? {};
          return {
            acsUserId: normalized?.acsUserId ?? "",
            token: normalized?.token ?? "",
            expiresOn: normalized?.expiresOn ?? "",
          };
        },
      }),

      createChatThread: builder.mutation<any, CreateChatThreadRequest>({
        query: (payload) => ({
          url: "api/communication/Chat/thread",
          method: "POST",
          data: payload,
        }),
      }),

      getChatMessages: builder.query<any[], GetChatMessagesRequest>({
        query: ({ threadId, userId, organiserId, take }) => ({
          url: "api/communication/Chat/messages",
          method: "GET",
          params: {
            threadId,
            userId,
            organiserId,
            take,
          },
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data?.messages ?? response?.data ?? response ?? [];
          return Array.isArray(normalized) ? normalized : [];
        },
      }),

      sendChatMessage: builder.mutation<any, SendChatMessageRequest>({
        query: (payload) => ({
          url: "api/communication/Chat/message",
          method: "POST",
          data: payload,
        }),
      }),

      uploadChatThreadAttachments: builder.mutation<
        ChatAttachmentResponse[],
        UploadChatThreadAttachmentsRequest
      >({
        query: ({ threadId, files }) => {
          const formData = new FormData();
          formData.append("threadId", threadId);
          files.forEach((file) => formData.append("files", file));

          return {
            url: "api/communication/ChatSettings/messages/attachments/upload",
            method: "POST",
            data: formData,
          };
        },
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? [];
          return Array.isArray(normalized) ? normalized : [];
        },
      }),

      downloadChatThreadAttachment: builder.query<Blob, DownloadChatThreadAttachmentRequest>({
        query: ({ threadId, userId, attachmentUrl, fileName, contentType }) => ({
          url: "api/communication/ChatSettings/messages/attachments/download",
          method: "GET",
          params: {
            threadId,
            userId,
            attachmentUrl,
            fileName,
            contentType,
          },
          responseType: "blob",
        }),
        transformResponse: (response: Blob) => response,
      }),

      editChatThreadMessage: builder.mutation<boolean, EditChatThreadMessageRequest>({
        query: (payload) => ({
          url: "api/communication/ChatSettings/messages/edit",
          method: "POST",
          data: payload,
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response;
          return !!normalized;
        },
      }),

      deleteChatThreadMessage: builder.mutation<boolean, DeleteChatThreadMessageRequest>({
        query: (payload) => ({
          url: "api/communication/ChatSettings/messages/delete",
          method: "POST",
          data: payload,
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response;
          return !!normalized;
        },
      }),

      markChatMessageRead: builder.mutation<any, MarkChatMessageReadRequest>({
        query: (payload) => ({
          url: "api/communication/Chat/messages/read",
          method: "POST",
          data: payload,
        }),
      }),

      getUnreadChatMessageCount: builder.query<
        UnreadChatMessageCountResponse,
        GetUnreadChatMessageCountRequest
      >({
        query: ({ threadId, userId }) => ({
          url: "api/communication/Chat/messages/unread-count",
          method: "GET",
          params: {
            threadId,
            userId,
          },
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? {};
          return {
            threadId: normalized?.threadId ?? "",
            userId: normalized?.userId ?? "",
            unreadCount:
              typeof normalized?.unreadCount === "number" ? normalized.unreadCount : 0,
            lastReadAt: normalized?.lastReadAt ?? null,
          };
        },
      }),

      getChatThreadById: builder.query<ChatThreadResponse, string>({
        query: (threadId) => ({
          url: `api/communication/Chat/thread/${threadId}`,
          method: "GET",
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? {};
          return {
            id: normalized?.id ?? "",
            resourceId: normalized?.resourceId ?? undefined,
            name: normalized?.name ?? "",
            designation: normalized?.designation ?? "",
            lastMessage: normalized?.lastMessage ?? "",
            time: normalized?.time ?? "",
            avatar: normalized?.avatar ?? "",
            conversationId: normalized?.conversationId ?? "",
            threadId: normalized?.threadId ?? "",
            topic: normalized?.topic ?? "",
            about: normalized?.about ?? "",
            participantIds: Array.isArray(normalized?.participantIds)
              ? normalized.participantIds
              : [],
            organiserId: normalized?.organiserId ?? "",
            lastActivityAt: normalized?.lastActivityAt ?? new Date().toISOString(),
          };
        },
      }),

      setChatBlockStatus: builder.mutation<boolean, SetChatBlockStatusRequest>({
        query: (payload) => ({
          url: "api/communication/ChatSettings/block",
          method: "POST",
          data: payload,
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response;
          return !!normalized;
        },
        invalidatesTags: ["ChatBlockSettings"],
      }),

      getBlockedChatUsers: builder.query<BlockedChatUserResponse[], void>({
        query: () => ({
          url: "api/communication/ChatSettings/blocked-users",
          method: "GET",
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? [];
          return Array.isArray(normalized) ? normalized : [];
        },
        providesTags: ["ChatBlockSettings"],
      }),

      addGroupChatParticipants: builder.mutation<
        ChatThreadResponse,
        AddGroupChatParticipantsRequest
      >({
        query: (payload) => ({
          url: "api/communication/ChatSettings/participants/add",
          method: "POST",
          data: payload,
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? {};
          return {
            id: normalized?.id ?? "",
            resourceId: normalized?.resourceId ?? undefined,
            name: normalized?.name ?? "",
            designation: normalized?.designation ?? "",
            lastMessage: normalized?.lastMessage ?? "",
            time: normalized?.time ?? "",
            avatar: normalized?.avatar ?? "",
            conversationId: normalized?.conversationId ?? "",
            threadId: normalized?.threadId ?? "",
            topic: normalized?.topic ?? "",
            about: normalized?.about ?? "",
            participantIds: Array.isArray(normalized?.participantIds)
              ? normalized.participantIds
              : [],
            organiserId: normalized?.organiserId ?? "",
            lastActivityAt: normalized?.lastActivityAt ?? "",
          };
        },
      }),

      updateGroupChatAbout: builder.mutation<ChatThreadResponse, UpdateGroupChatAboutRequest>({
        query: (payload) => ({
          url: "api/communication/ChatSettings/group/about",
          method: "POST",
          data: payload,
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? {};
          return {
            id: normalized?.id ?? "",
            resourceId: normalized?.resourceId ?? undefined,
            name: normalized?.name ?? "",
            designation: normalized?.designation ?? "",
            lastMessage: normalized?.lastMessage ?? "",
            time: normalized?.time ?? "",
            avatar: normalized?.avatar ?? "",
            conversationId: normalized?.conversationId ?? "",
            threadId: normalized?.threadId ?? "",
            topic: normalized?.topic ?? "",
            about: normalized?.about ?? "",
            participantIds: Array.isArray(normalized?.participantIds)
              ? normalized.participantIds
              : [],
            organiserId: normalized?.organiserId ?? "",
            lastActivityAt: normalized?.lastActivityAt ?? "",
          };
        },
      }),

      updateGroupChatProfilePhoto: builder.mutation<
        ChatThreadResponse,
        FormData
      >({
        query: (formData) => ({
          url: "api/communication/ChatSettings/group/profile-photo",
          method: "POST",
          data: formData,
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? {};
          return {
            id: normalized?.id ?? "",
            resourceId: normalized?.resourceId ?? undefined,
            name: normalized?.name ?? "",
            designation: normalized?.designation ?? "",
            lastMessage: normalized?.lastMessage ?? "",
            time: normalized?.time ?? "",
            avatar: normalized?.avatar ?? "",
            conversationId: normalized?.conversationId ?? "",
            threadId: normalized?.threadId ?? "",
            topic: normalized?.topic ?? "",
            about: normalized?.about ?? "",
            participantIds: Array.isArray(normalized?.participantIds)
              ? normalized.participantIds
              : [],
            organiserId: normalized?.organiserId ?? "",
            lastActivityAt: normalized?.lastActivityAt ?? "",
          };
        },
      }),

      setGroupChatAdmin: builder.mutation<GroupChatParticipantsResponse, SetGroupChatAdminRequest>({
        query: (payload) => ({
          url: "api/communication/ChatSettings/participants/set-admin",
          method: "POST",
          data: payload,
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? {};
          const rawMembers = Array.isArray(normalized?.members) ? normalized.members : [];
          return {
            threadId: normalized?.threadId ?? "",
            organiserId: normalized?.organiserId ?? "",
            adminIds: Array.isArray(normalized?.adminIds) ? normalized.adminIds : [],
            participantIds: Array.isArray(normalized?.participantIds) ? normalized.participantIds : [],
            members: rawMembers,
          };
        },
      }),

      removeGroupChatParticipant: builder.mutation<
        ChatThreadResponse,
        { threadId: string; participantPortalUserId: string }
      >({
        query: (payload) => ({
          url: "api/communication/ChatSettings/participants/remove",
          method: "POST",
          data: payload,
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? {};
          return {
            id: normalized?.id ?? "",
            resourceId: normalized?.resourceId ?? undefined,
            name: normalized?.name ?? "",
            designation: normalized?.designation ?? "",
            lastMessage: normalized?.lastMessage ?? "",
            time: normalized?.time ?? "",
            avatar: normalized?.avatar ?? "",
            conversationId: normalized?.conversationId ?? "",
            threadId: normalized?.threadId ?? "",
            topic: normalized?.topic ?? "",
            about: normalized?.about ?? "",
            participantIds: Array.isArray(normalized?.participantIds)
              ? normalized.participantIds
              : [],
            organiserId: normalized?.organiserId ?? "",
            lastActivityAt: normalized?.lastActivityAt ?? "",
          };
        },
      }),

      getGroupChatParticipants: builder.query<GroupChatParticipantsResponse, string>({
        query: (threadId) => ({
          url: "api/communication/ChatSettings/participants",
          method: "GET",
          params: {
            threadId,
          },
        }),
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? {};
          const rawMembers = Array.isArray(normalized?.members) ? normalized.members : [];

          return {
            threadId: normalized?.threadId ?? "",
            organiserId: normalized?.organiserId ?? "",
            adminIds: Array.isArray(normalized?.adminIds) ? normalized.adminIds : [],
            participantIds: Array.isArray(normalized?.participantIds)
              ? normalized.participantIds
              : [],
            members: rawMembers.map((item: any) => ({
              ...item,
              id:
                item?.id ??
                item?.portalUserId ??
                item?.email ??
                item?.userId ??
                "",
              portalUserId:
                item?.portalUserId ??
                item?.email ??
                item?.userId ??
                item?.id ??
                "",
              name:
                item?.name ??
                item?.displayName ??
                item?.fullName ??
                "",
              avatar:
                item?.avatar ??
                item?.profileImage ??
                item?.profilePicture ??
                item?.image ??
                item?.photoUrl ??
                item?.profilePictureUrl ??
                item?.profilePhotoUrl ??
                "",
            })),
          };
        },
      }),

      deleteGroupChatThread: builder.mutation<boolean, { threadId: string }>({
        query: (payload) => ({
          url: "api/communication/ChatSettings/group/delete",
          method: "POST",
          data: payload,
        }),
        transformResponse: (response: any) => {
          return response?.data ?? response?.success ?? true;
        },
      }),
      //
      getArticles: builder.query<
        IPaginatedResponse<IArticle>,
        {
          userId: number;
          postType: string;
          pageNumber: number;
          pageSize: number;
          sortOrder: string;
        }
      >({
        query: ({ userId, postType, pageNumber, pageSize, sortOrder }) => ({
          url: `api/People/articles`,
          method: "GET",
          params: { userId, postType, pageNumber, pageSize, sortOrder },
        }),
        providesTags: ["bookmarkArticle", "removeBookmarkArticle"],
      }),
      bookmarkArticle: builder.mutation<
        void,
        { userId: number; articleId: number; postType: string }
      >({
        query: ({ userId, articleId, postType }) => ({
          url: `api/People/articles/${articleId}/bookmark`,
          method: "POST",
          params: { userId, postType },
        }),
        invalidatesTags: ["bookmarkArticle", "removeBookmarkArticle"],
      }),
      removeBookmark: builder.mutation<
        void,
        { userId: number; articleId: number; postType: string }
      >({
        query: ({ userId, articleId, postType }) => ({
          url: `api/People/articles/${articleId}/bookmark`,
          method: "DELETE",
          params: { userId, postType },
        }),
        invalidatesTags: ["bookmarkArticle", "removeBookmarkArticle"],
      }),

      //HelpDesk
      getHelpdeskFormMetadata: builder.query<
        {
          areasOfService: { label: string; value: string }[];
          industries: { label: string; value: string }[];
          locations: { label: string; value: string }[];
        },
        void
      >({
        query: () => ({
          url: "api/content/Helpdesk/form-metadata",
          method: "GET",
        }),
        transformResponse: (response: any) => ({
          areasOfService: response?.data?.areasOfService ?? [],
          industries: response?.data?.industries ?? [],
          locations: response?.data?.locations ?? [],
        }),
      }),

      getDocFormMetadata: builder.query<
        { documents: { label: string; value: string }[] },
        void
      >({
        query: () => ({
          url: "api/content/Helpdesk/docform/form-metadata",
          method: "GET",
        }),
        transformResponse: (response: any) => {
          console.log("DocForm API Response:", response);
          return {
            documents: response?.data?.documents || [],
          };
        },
      }),

      createRfp: builder.mutation<
        { message: string },
        {
          areaOfServiceId: number;
          industryId: number;
          projectTitle: string;
          projectTimeline: string;
          purposeObjectives: string;
          scopeOfWork: string;
        }
      >({
        query: (rfpData) => ({
          url: "api/content/Helpdesk/rfp",
          method: "POST",
          data: rfpData,
        }),
      }),

      createFae: builder.mutation<
        { message: string },
        {
          areaOfServiceId: number;
          industryId: number;
          locationId: number;
          description: string;
          industryName?: string;
        }
      >({
        query: (faeData) => ({
          url: "api/content/Helpdesk/fae",
          method: "POST",
          data: faeData,
        }),
      }),

      createDocForm: builder.mutation<
        { message: string },
        {
          documentsIds: number[];
          description: string;
        }
      >({
        query: (docFormData) => ({
          url: "api/content/Helpdesk/docform",
          method: "POST",
          data: docFormData,
        }),
      }),

      createFeedbackForm: builder.mutation<
        { message: string },
        {
          subject: string;
          description: string;
        }
      >({
        query: (feedbackData) => ({
          url: "api/content/Helpdesk/feedbackform",
          method: "POST",
          data: feedbackData,
        }),
      }),
      
      //Get Profile
      getProfile: builder.query<IViewProfile, number>({
        query: (userId) => ({
          url: `api/people/Profile/myprofile?userid=${userId}`,
          method: "GET",
        }),
        providesTags: ["Profile"],
      }),
      updateProfilePhoto: builder.mutation<
        { profilePhotoUrl: string },
        FormData
        >({
        query: (formData) => ({
        url: "api/people/Profile/myProfile/photo",
        method: "PUT",
        data: formData,
        }),
        invalidatesTags: ["Profile"],
        }),
      updateProfileAboutSkills: builder.mutation<
        { message: string },
        { employeeId: number; about: string; skills: string }
      >({
        query: (payload) => ({
          url: "api/people/Profile/myProfile/about-skills",
          method: "PUT",
          data: payload,
        }),
        invalidatesTags: ["Profile"],
      }),
      updateProfileProfessional: builder.mutation<
        { message: string },
        {
          employeeId: number;
          currentJobTitle: string;
          currentCompany: string;
          currentIndustry: string;
          currentWorkLocation: string;
        }
      >({
        query: (payload) => ({
          url: "api/people/Profile/myProfile/professional",
          method: "PUT",
          data: payload,
        }),
        invalidatesTags: ["Profile"],
      }),
      updateProfileProjects: builder.mutation<
        { message: string },
        { employeeId: number; keyProjects: string }
      >({
        query: (payload) => ({
          url: "api/people/Profile/myProfile/projects",
          method: "PUT",
          data: payload,
        }),
        invalidatesTags: ["Profile"],
      }),
      updateProfileEducations: builder.mutation<
        { message: string },
        {
          employeeId: number;
          educations: Array<{
            educationId: number;
            institution: string;
            course: string;
            year: number;
          }>;
        }
      >({
        query: (payload) => ({
          url: "api/people/Profile/myProfile/educations",
          method: "PUT",
          data: payload,
        }),
        invalidatesTags: ["Profile"],
      }),
      updateProfileContact: builder.mutation<
        { message: string },
        {
          employeeId: number;
          primaryEmail: string;
          phoneCode: string;
          mobilePhone: string;
          linkedInLink: string;
          currentLocation: string;
        }
      >({
        query: (payload) => ({
          url: "api/people/Profile/myProfile/contact",
          method: "PUT",
          data: payload,
        }),
        invalidatesTags: ["Profile"],
      }),
      deactivateMyProfile: builder.mutation<{ message?: string }, void>({
        query: () => ({
          url: "api/people/Profile/myProfile/deactivate",
          method: "PATCH",
        }),
        invalidatesTags: ["Profile"],
      }),
      deleteMyProfile: builder.mutation<{ message?: string }, void>({
        query: () => ({
          url: "api/people/Profile/myProfile",
          method: "DELETE",
        }),
        invalidatesTags: ["Profile"],
      }),

      //Landing Page
      getLandingPageDetails: builder.query<ILandingPageResponse, void>({
        query: () => ({
          url: `api/content/LandingPage`,
          method: "GET",
        })
      }),

      findMyAccount: builder.query<
        AlumniRegisterResponse,
        RegisterForm
      >({
        query: (accountData) => ({
          url: `api/content/LandingPage/find-account`,
          method: "GET",   
          params: accountData     
        }),
      }),
      createContactSupport: builder.mutation<any, FormData>({
        query: (formData) => ({
          url: `api/content/LandingPage/contact-support`,
          method: "POST",
          data: formData,
        }),
      }),

      //Profile Setup
      profileSetup: builder.mutation<{ message: string }, FormData>({
        query: (formData) => ({
          url: "api/people/profile/account-setup",
          method: "POST",
          data: formData,
        }),
      }),
      getProfileSetupData: builder.query<IProfileSetupDataResponse, string>({
        query: (emailId) => ({
          url: `api/people/Profile/alumni?emailId=${emailId}`,
          method: "GET",
        })
      }),
      getCountryList: builder.query<[], void>({
        query: () => ({
          url: `https://countriesnow.space/api/v0.1/countries/positions`,
          method: "GET",
        })
      }),
        getCityList: builder.mutation<[], void>({
        query: (payload) => ({
          url: `https://countriesnow.space/api/v0.1/countries/cities`,
          method: "POST",
          data: payload
        })
      }), 

    initiateCiamRegistration: builder.mutation<
        CiamRegistrationResponse,
        InitiateCiamRegistrationRequest
      >({
        query: (registrationData) => ({
          url: `api/content/LandingPage/RegisterAlumni`,
          method: "POST",
          data: registrationData,
        }),
      }),

      //Notifications APIs
      getNotifications: builder.query<NotificationItemModel[], number>({
        query: (userId) => ({
          url: "api/people/Notifications",
          method: "GET",
          params: { userId, pageSize: 0, page: 1 },
        }),
        transformResponse: (response: { data: NotificationsPageDto; statusCode: number }) => {
          const items = response?.data?.items ?? [];
          return items.map((item: NotificationListItemDto): NotificationItemModel => {
            const now = new Date();
            const created = new Date(item.createdAtUtc);
            const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const createdDate = new Date(created.getFullYear(), created.getMonth(), created.getDate());
            const diffDays = Math.floor((nowDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
            const group: NotificationGroup = diffDays === 0 ? "Today" : diffDays === 1 ? "Yesterday" : "Earlier this week";
            return {
              id: String(item.notificationId),
              title: item.title,
              message: item.message,
              timeAgo: item.timeDisplay,
              group,
              isRead: item.isMarkedRead,
              imageUrl: item.photoUrl ?? "/assets/img/notification-img.jpg",
              entityId: item.entityId,
              type: item.type,
            };
          });
        },
        providesTags: ["Notifications"],
      }),

      markAllNotificationsAsRead: builder.mutation<void, number>({
        query: (userId) => ({
          url: "api/people/Notifications/readAll",
          method: "POST",
          params: { userId },
        }),
        invalidatesTags: ["Notifications"],      
      }),

      getPendingReviewRequests: builder.query<PendingReviewRequestsResponse, void>({
        query: () => ({
          url: "api/content/Admin/pending-review-requests",
          method: "GET",
        }),
      }),

      getOnboardingManualDetails: builder.query<OnboardingManualDetailsResponse, number>({
        query: (employeeId) => ({
          url: `api/people/Admin/${employeeId}/onboarding/manual`,
          method: "GET",
        }),
      }),

      getAdminContactSupportRequest: builder.query<ContactSupportRequestDetailResponse, number>({
        query: (requestId) => ({
          url: `api/content/Admin/${requestId}/contact-support`,
          method: "GET",
        }),
      }),

      acknowledgeContactSupportRequest: builder.mutation<ApiResponse<boolean>, { requestId: number; type: string }>({
        query: ({ requestId, type }) => ({
          url: `api/content/Admin/${requestId}/acknowledge?type=${encodeURIComponent(type)}`,
          method: "PATCH",
        }),
      }),

      approveOnboardingRequest: builder.mutation<ApiResponse<boolean>, number>({
        query: (employeeId) => ({
          url: `api/people/Admin/${employeeId}/onboarding/approve`,
          method: "PATCH",
        }),
      }),

      rejectOnboardingRequest: builder.mutation<ApiResponse<boolean>, { employeeId: number; rejectReason: string }>({
        query: ({ employeeId, rejectReason }) => ({
          url: `api/people/Admin/${employeeId}/onboarding/reject?rejectReason=${encodeURIComponent(rejectReason)}`,
          method: "PATCH",
        }),
      }),

      getAdminFeedbacks: builder.query<AdminFeedbackResponse, { sortOrder: string; pageNumber: number; pageSize: number }>({
        query: ({ sortOrder, pageNumber, pageSize }) => ({
          url: `api/content/Admin/feedbacks?sortOrder=${encodeURIComponent(sortOrder)}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
          method: "GET",
        }),
      }),

      getAdminAlumniDirectory: builder.query<
        IAdminAlumniDirectoryResponse,
        IGetProfileRequest & { isBlocked?: boolean }
      >({
        query: (params) => ({
          url: "api/people/Admin/alumni-directory",
          method: "GET",
          params,
        }),
      }),

      exportAdminAlumniDirectory: builder.mutation<
        AdminAlumniDirectoryExportResponse,
        AdminAlumniDirectoryExportRequest
      >({
        queryFn: async (params) => {
          try {
            const response = await appAxiosInstance.get(
              "api/people/Admin/alumni-directory/export",
              {
                params: {
                  ...params,
                  employeeIds: params.employeeIds?.length
                    ? params.employeeIds.join(",")
                    : undefined,
                },
                responseType: "blob",
                headers: {
                  Accept:
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                },
              }
            );

            const contentDisposition = response.headers?.["content-disposition"] as string | undefined;
            const fileNameMatch = contentDisposition?.match(
              /filename\*?=(?:UTF-8''|\")?([^\";]+)/i
            );
            const fileName = fileNameMatch?.[1]
              ? decodeURIComponent(fileNameMatch[1].replace(/\"/g, "").trim())
              : undefined;

            return {
              data: {
                blob: response.data as Blob,
                fileName,
              },
            };
          } catch (axiosError: any) {
            return {
              error: {
                status: axiosError?.response?.status,
                data: axiosError?.response?.data || axiosError?.message,
              },
            };
          }
        },
      }),

      deactivateAdminAlumni: builder.mutation<
        ApiResponse<boolean>,
        { employeeIds: number[] }
      >({
        query: (payload) => ({
          url: "api/people/Admin/alumni/deactivate",
          method: "PATCH",
          data: payload,
        }),
      }),

      blockAdminAlumni: builder.mutation<
        ApiResponse<boolean>,
        { employeeIds: number[] }
      >({
        query: (payload) => ({
          url: "api/people/Admin/alumni/block",
          method: "PATCH",
          data: payload,
        }),
      }),

      unblockAdminAlumni: builder.mutation<
        ApiResponse<boolean>,
        { employeeIds: number[] }
      >({
        query: (payload) => ({
          url: "api/people/Admin/alumni/unblock",
          method: "PATCH",
          data: payload,
        }),
      }),

      sendBulkEmail: builder.mutation<ApiResponse<SendBulkEmailResponse>, SendBulkEmailRequest>({
        query: (bulkEmailRequest) => ({
          url: `api/communication/admin/send-bulk-email`,
          method: "POST",
          data: bulkEmailRequest,
        }),
      }),

      uploadBulkEmailAttachment: builder.mutation<UploadBulkEmailAttachmentResponse, File>({
        query: (file) => {
          const formData = new FormData();
          formData.append("file", file);

          return {
            url: "api/communication/admin/upload-bulk-email-attachment",
            method: "POST",
            data: formData,
          };
        },
        transformResponse: (response: any) => {
          const normalized = response?.data ?? response ?? {};
          return {
            fileName: normalized?.fileName ?? "",
            contentType: normalized?.contentType ?? "application/octet-stream",
            attachmentPath: normalized?.attachmentPath ?? "",
          };
        },
      }),

      getBulkEmailDrafts: builder.query<ApiResponse<BulkEmailDraft[]>, void>({
        query: () => ({
          url: "api/communication/admin/bulk-email-drafts",
          method: "GET",
          params: {
            status: "Pending",
          },
        }),
        providesTags: ["BulkEmailDrafts"],
      }),

      getBulkEmailHistory: builder.query<ApiResponse<BulkEmailDraft[]>, void>({
        query: () => ({
          url: "api/communication/admin/bulk-email-history",
          method: "GET",
        }),
        providesTags: ["BulkEmailHistory"],
      }),

      saveBulkEmailDraft: builder.mutation<ApiResponse<SaveBulkEmailDraftResponse>, SaveBulkEmailDraftRequest>({
        query: (request) => ({
          url: "api/communication/admin/save-bulk-email-draft",
          method: "POST",
          data: request,
        }),
        invalidatesTags: ["BulkEmailDrafts"],
      }),

      deleteBulkEmailDraft: builder.mutation<ApiResponse<DeleteBulkEmailDraftResponse>, DeleteBulkEmailDraftRequest>({
        query: (request) => ({
          url: "api/communication/admin/delete-bulk-email-draft",
          method: "POST",
          data: request,
        }),
        invalidatesTags: ["BulkEmailDrafts"],
      }),
    };
  },
});

export const {
  useGetProfileQuery,
  useApplyJobMutation,
  useGetAluminiProfileListQuery,
  useFollowAlumniMutation,
  useUnfollowAlumniMutation,
  useGetLocationsQuery,
  useGetDesignationsQuery,
  useGetFunctionsQuery,
  useGetCompanyListQuery,
  useGetLastDesignationListQuery,
  useGetIndustriesQuery,
  useGetMasterMenuQuery,
  useLazyGetMasterMenuQuery,
  useGetHomeBannerQuery,
  useLazyGetHomeBannerQuery,
  useLazyGetHomeWhatsNewQuery,
  useLazyGetEventQuery,
  useEventRegistrationMutation,
  useBookmarkEventsMutation,
  useBookmarkWhatsNewMutation,
  useGetArticlesQuery,
  useBookmarkArticleMutation,
  useRemoveBookmarkMutation,
  useGetHelpdeskFormMetadataQuery,
  useGetDocFormMetadataQuery,
  useCreateRfpMutation,
  useCreateFaeMutation,
  useCreateDocFormMutation,
  useCreateFeedbackFormMutation,
  useGetMarketplaceJobsQuery,
  useGetMarketplaceJobsSearchQuery,
  useGetMarketplaceAlumniProfileQuery,
  useUpdateMarketplaceJobMutation,
  useGetChatUsersQuery,
  useLazySearchChatUsersQuery,
  useLazyGetChatMessagesQuery,
  useSendChatMessageMutation,
  useUploadChatThreadAttachmentsMutation,
  useLazyDownloadChatThreadAttachmentQuery,
  useEditChatThreadMessageMutation,
  useDeleteChatThreadMessageMutation,
  useMarkChatMessageReadMutation,
  useLazyGetUnreadChatMessageCountQuery,
  useSetChatBlockStatusMutation,
  useGetBlockedChatUsersQuery,
  useGetChatThreadByIdQuery,
  useLazyGetChatThreadByIdQuery,
  useCreateChatThreadMutation,
  useAddGroupChatParticipantsMutation,
  useUpdateGroupChatAboutMutation,
  useSendBulkEmailMutation,
  useUploadBulkEmailAttachmentMutation,
  useGetBulkEmailDraftsQuery,
  useGetBulkEmailHistoryQuery,
  useSaveBulkEmailDraftMutation,
  useDeleteBulkEmailDraftMutation,
  useUpdateGroupChatProfilePhotoMutation,
  useSetGroupChatAdminMutation,
  useRemoveGroupChatParticipantMutation,
  useGetGroupChatParticipantsQuery,
  useLazyGetGroupChatParticipantsQuery,
  useDeleteGroupChatThreadMutation,
  useUpdateProfilePhotoMutation,
  useUpdateProfileAboutSkillsMutation,
  useUpdateProfileProfessionalMutation,
  useUpdateProfileProjectsMutation,
  useUpdateProfileEducationsMutation,
  useUpdateProfileContactMutation,
  useDeactivateMyProfileMutation,
  useDeleteMyProfileMutation,
  useLazyGetProfileQuery,
  useGetCountryListQuery,
  useGetCityListMutation,
  useGetOrCreateChatTokenMutation,
  useGetNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
  useGetPendingReviewRequestsQuery,
  useGetOnboardingManualDetailsQuery,
  useGetAdminContactSupportRequestQuery,
  useAcknowledgeContactSupportRequestMutation,
  useApproveOnboardingRequestMutation,
  useRejectOnboardingRequestMutation,
  useGetAdminFeedbacksQuery,
  useGetAdminAlumniDirectoryQuery,
  useExportAdminAlumniDirectoryMutation,
  useDeactivateAdminAlumniMutation,
  useBlockAdminAlumniMutation,
  useUnblockAdminAlumniMutation,
} = alumniPortalApi;

// Public API for endpoints that don't require authentication
export const publicApi = createApi({
  reducerPath: "publicApi",
  baseQuery: publicAxiosBaseQuery({ baseUrl: "/" }),
  endpoints: (builder: EndpointBuilder<any, any, any>) => {
    return {
      //Landing Page
      getLandingPageDetails: builder.query<ILandingPageResponse, void>({
        query: () => ({
          url: `api/content/LandingPage`,
          method: "GET",
        }),
      }),
      findMyAccount: builder.query<
        AlumniRegisterResponse,
        RegisterForm
      >({
        query: (accountData) => ({
          url: `api/content/LandingPage/find-account`,
          method: "GET",
          params: accountData,
        }),
      }),
      createContactSupport: builder.mutation<any, FormData>({
        query: (formData) => ({
          url: `api/content/LandingPage/contact-support`,
          method: "POST",
          data: formData,
        }),
      }),
      //Profile Setup
      profileSetup: builder.mutation<{ message: string }, FormData>({
        query: (formData) => ({
          url: "api/people/profile/account-setup",
          method: "POST",
          data: formData,
        }),
      }),
      getProfileSetupData: builder.query<IProfileSetupDataResponse, string>({
        query: (emailId) => ({
          url: `api/people/Profile/alumni?emailId=${emailId}`,
          method: "GET",
        }),
      }),
      initiateCiamRegistration: builder.mutation<
        CiamRegistrationResponse,
        InitiateCiamRegistrationRequest
      >({
        query: (registrationData) => ({
          url: `api/content/LandingPage/RegisterAlumni`,
          method: "POST",
          data: registrationData,
        }),
      }),
      createJobPosting: builder.mutation<any, FormData>({
        query: (formData) => ({
          url: `api/Careers/MarketplaceJobs/create`,
          method: "POST",
          data: formData,
        }),
      }),
    };
  },
});

export const {
  useLazyGetLandingPageDetailsQuery,
  useLazyFindMyAccountQuery,
  useCreateContactSupportMutation,
  useLazyGetProfileSetupDataQuery,
  useProfileSetupMutation,
  useInitiateCiamRegistrationMutation,
  useCreateJobPostingMutation,
} = publicApi;
